<template>
  <div class="irr-calculator" v-loading="loading">
    <div class="assistantTitle">
      <span>
        个券 IRR 计算
        <span v-if="helpConfig" class="irr-help-link" @click="helpVisible = true">{{ helpConfig.title }}</span>
      </span>
    </div>

    <p v-if="error" class="notice" role="alert">{{ error }}</p>

    <template v-if="result">
      <div class="toolbar">
        <div class="toolbar-row">
        <div class="field">
          <span class="field-label">预测方式</span>
          <CButtonsSeparate
            class="preset-buttons"
            :dButtons1="presetLabels"
            :dButtons1_current="currentPresetLabel"
            :disabled="result.yield_only"
            @Cbtns_sep="onPreset" />
        </div>
        <div v-if="inputs.preset === 'forecast' && result.forecast_anchor_available" class="field">
          <el-checkbox v-model="inputs.forecast_anchor" @change="recompute()">按最新实际校准</el-checkbox>
          <el-tooltip placement="top" effect="dark"
            content="保留评估报告逐年的变化幅度，起点换成最新实际 TTM（评估报告对当年的预测与实际有偏差时适用）">
            <i class="el-icon-question hint"></i>
          </el-tooltip>
        </div>
        <div v-if="modeLabels.length > 1" class="field">
          <span class="field-label">计算口径</span>
          <CButtonsSeparate :dButtons1="modeLabels" :dButtons1_current="currentModeLabel" @Cbtns_sep="onMode" />
        </div>
        </div>
        <div class="toolbar-row">
        <div class="field">
          <span class="field-label">剩余期限</span>
          <el-input v-model="remainYearsText" size="mini" class="short-input" @change="onRemainYears" />
          <span class="field-unit">年</span>
        </div>
        <div class="field">
          <el-checkbox v-model="inputs.include_residual" :disabled="!residualAmount" @change="recompute()">含残值</el-checkbox>
        </div>
        <div class="field scenario">
          <el-select popper-class="irr-select-popper" v-model="currentScenario" size="mini" placeholder="我的方案" clearable class="scenario-select"
            @change="openScenario">
            <el-option v-for="s in namedScenarios" :key="s.name" :label="s.name" :value="s.name" />
          </el-select>
          <el-button size="mini" :disabled="scenarioReadOnly" @click="saveDialogVisible = true">保存方案</el-button>
          <el-button size="mini" :disabled="!currentScenario || scenarioReadOnly" @click="removeScenario">删除</el-button>
          <el-button size="mini" @click="resetInputs">恢复默认</el-button>
        </div>
        </div>
      </div>

      <div v-if="inputs.preset === 'custom' && !result.yield_only" class="custom-strip">
        <span class="field-label">自定义假设（每个预测年统一适用）</span>
        <div v-for="f in customFields" :key="f.key" class="field">
          <span class="field-label">{{ f.label }}</span>
          <el-input v-model="customText[f.key]" size="mini" class="short-input" @change="onCustom(f.key)" />
          <span class="field-unit">%</span>
          <span v-if="f.stat" class="hint">历史平均 {{ statValue(f.stat, 'avg') }}</span>
        </div>
      </div>

      <p v-if="updateNotice" class="notice update-notice">{{ updateNotice }}</p>
      <p v-if="jumpNotice" class="notice update-notice">{{ jumpNotice }}</p>
      <p v-if="forecastCheckNotice" class="notice">{{ forecastCheckNotice }}</p>
      <p v-if="scenarioReadOnly" class="notice">方案服务暂时只读：可以打开已保存的方案，暂不能保存。</p>
      <p v-if="result.cash_from_yield && !result.yield_only" class="notice">该基金暂无季报及同业态可供分配比例，现金流暂按招募说明书预测分派率计算。</p>
      <p v-if="result.forecast_scope_cut_from" class="notice">评估报告 {{ result.forecast_scope_cut_from }} 年起的预测中，组合层与子资产合计对不上（子资产不全或重复），这几年未采用。</p>
      <p v-if="!operatingRows.length && !result.yield_only" class="notice">该基金暂无经营指标（租金、出租率等）数据，可直接调整下方「营业收入增速」逐年预测。</p>
      <p v-if="result.leverage && result.leverage.interest_without_debt" class="notice">该基金财报未单列有息负债，但有利息支出（如租赁负债等计息项目），预测期利息按近一年实际保持不变。</p>
      <p v-if="principalPlan && principalPlan.years.length" class="notice">偿还本金 {{ principalPlanYears }} 年按{{ principalPlanSource }}披露的借款还本计划，与近一年净还款的差额计入可供分配。</p>
      <p v-if="principalPlan && principalPlan.years.length && principalPlan.has_quality_issue" class="notice">该还本计划未完全通过原文核对（可能有期次未抽到，或报告排版导致引文与原文对不上），仅供参考，可直接修改「偿还本金」。</p>
      <p v-if="principalPlan && principalPlan.refinanced_years.length" class="notice">原文约定 {{ principalPlan.refinanced_years.join('、') }} 年到期一次归还剩余本金约 {{ (Number(principalPlan.refinanced_amount || 0) / 1e8).toLocaleString('zh-CN', { maximumFractionDigits: 2 }) }} 亿元，默认按续贷处理、沿用近一年还款额；如需按到期全额偿还，可修改该年「偿还本金」。</p>
      <p v-if="result.yield_only" class="notice">该基金暂无季报与评估预测，仅按系统分红率与增速计算，营业收入各行暂不可编辑。</p>

      <div v-for="section in tableSections" :key="section.key" class="section-card">
        <div class="section-title">{{ section.title }}
          <el-checkbox v-if="section.split" v-model="splitAssets" class="split-toggle">按项目拆分</el-checkbox>
          <span v-if="section.hint" class="hint">{{ section.hint }}</span>
          <span v-if="section.split && splitAssets" class="hint">各项目起点占比按 {{ result.assets_through }} 前近四季面价租金 × 出租面积（能源、高速为各项目收入）。{{ result.assets_share_path === 'report' ? '预测期按评估报告分项目预测的逐年变化分配合计' : '评估报告的分项目逐年预测不全，预测期各项目先按起点占比分配合计' }}；可直接改各项目的租金、出租率或营业收入，合计营业收入和下方现金流、IRR 会同步重算；改合计则按占比分摊回各项目。</span>
        </div>
        <div class="table-wrap">
          <table class="irr-table">
            <thead>
              <tr>
                <th class="sticky label-col">主要参数</th>
                <th class="sticky stat-col">历史平均</th>
                <th class="sticky stat-col">上限（90分位）</th>
                <th class="sticky stat-col">下限（10分位）</th>
                <th v-for="c in historyColumns" :key="c.period" class="actual-col">
                  {{ c.label }}<span class="sub">实际</span>
                </th>
                <th v-for="(y, i) in result.years" :key="y" :class="{ 'actual-col': i === 0 }">
                  <template v-if="i === 0">{{ result.actual_through || y + 'e' }}<span class="sub">TTM 起点</span></template>
                  <template v-else>{{ y }}e
                    <span v-if="i === result.years.length - 1 && result.last_year_fraction < 0.999" class="sub">
                      {{ (result.last_year_fraction * 12).toFixed(0) }} 个月
                    </span>
                    <span v-else class="sub">{{ growthSourceLabel(i) }}</span>
                  </template>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in section.rows" :key="row.key"
                :class="{ strong: row.strong, highlight: row.highlight, ratio: row.kind === 'rate' && !row.highlight }">
                <td class="sticky label-col">
                  <span>{{ row.label }}</span><span v-if="row.unit" class="unit">{{ row.unit }}</span>
                  <el-tooltip v-if="row.note" :content="row.note" placement="top" effect="dark">
                    <span class="note-mark">报告假设</span>
                  </el-tooltip>
                  <el-dropdown v-if="editable(row, 1)" trigger="click" size="mini" class="row-menu"
                    @command="(cmd) => onRowAction(row, cmd)">
                    <span class="row-menu-trigger" :aria-label="row.label + ' 快捷操作'"><i class="el-icon-more"></i></span>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item v-for="a in rowActions" :key="a.command" :command="a.command">{{ a.label }}</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </td>
                <td class="sticky stat-col">{{ stat(row, 'avg') }}</td>
                <td class="sticky stat-col">{{ stat(row, 'p90') }}</td>
                <td class="sticky stat-col">{{ stat(row, 'p10') }}</td>
                <td v-for="c in historyColumns" :key="c.period" class="actual-col">{{ display(row, c.values[row.key]) }}</td>
                <td v-for="(y, i) in result.years" :key="y"
                  :class="{ edited: isEdited(row.key, y), 'actual-col': i === 0 }">
                  <input v-if="editable(row, i)" class="cell-input" :value="display(row, cell(row, i))"
                    :aria-label="row.label + ' ' + y" :data-row="row.key" :data-col="i"
                    @change="onCell(row, y, $event.target.value)" @keydown="onCellKey(row, i, $event)"
                    @paste="onPaste(row, i, $event)" />
                  <span v-else>{{ display(row, cell(row, i)) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="section.key === (operatingRows.length ? 'op' : 'cash') && assumptionGroups.length" class="assumptions">
          <span class="irr-help-link" @click="assumptionsOpen = !assumptionsOpen">
            {{ assumptionsOpen ? '收起' : '查看' }}评估报告收益法假设（{{ assumptionGroups.length }} 项资产，原文摘录，仅供参考）
          </span>
          <ul v-if="assumptionsOpen">
            <li v-for="g in assumptionGroups" :key="g.owner">
              <b>{{ g.owner }}</b>
              <span v-if="g.occ.length">出租率：{{ g.occ.join('；') }}</span>
              <span v-if="g.rent.length">租金增长率：{{ g.rent.join('；') }}</span>
            </li>
          </ul>
        </div>
      </div>
      <p class="legend">
        <span class="legend-key"><i class="swatch actual"></i>实际数</span>
        <span class="legend-key"><i class="swatch edited"></i>已修改</span>
        <span>金额单位：万元；改动后自动重算；行名旁「⋯」可向右填充、按比例调整、复制，也可从 Excel 粘贴一行。</span>
        <span>{{ presetNote }}{{ forecastSourceText }}。</span>
        <span>{{ sourceNote }}</span>
        <span v-if="helpConfig" class="irr-help-link" @click="helpVisible = true">完整口径见{{ helpConfig.title }}</span>
      </p>

      <div class="assistantTitle results-title">估值结果</div>
      <div class="results">
        <div class="result-card summary-card">
          <div class="card-title">主要成果汇总</div>
          <div class="summary-grid">
            <div class="metric"><span>IRR</span><b class="headline">{{ pct(result.irr) }}</b></div>
            <div class="metric"><span>NPV（每份）</span><b class="headline">{{ result.npv ? num(result.npv.per_unit, 3) : '--' }}</b></div>
            <div class="metric"><span>本金摊余</span><b>{{ pct(result.principal_return) }}</b>
              <em>第一年预测分派率 − IRR</em></div>
            <div class="metric"><span>基金单位目前价格</span><b>{{ num(result.price.close, 3) }}</b></div>
            <div class="metric"><span>系统 IRR（简化两段式）</span><b>{{ pct(systemIrr) }}</b>
              <em>近 12 个月分红 + 线性增速，仅作对照</em></div>
            <div class="metric"><span>NPV 占股价比例</span><b>{{ result.npv ? num(result.npv.to_price, 2) : '--' }}</b></div>
            <div class="metric"><span>招募书预测 IRR</span><b>{{ result.disclosed_irr ? pct(result.disclosed_irr.irr) : '--' }}</b>
              <em>{{ result.disclosed_irr ? '按发行价测算，仅作对照' : '招募书未披露或暂未取得' }}</em></div>
            <div v-if="savedIrr !== null" class="metric"><span>方案保存时 IRR</span><b>{{ pct(savedIrr) }}</b></div>
          </div>
        </div>
        <div class="result-card model-card">
          <div class="section-title">参数计算器</div>
          <div class="mc-row">
            <span class="mc-label">预设模型选择</span>
            <div class="mc-body"><CButtonsSeparate :dButtons1="modelLabels" :dButtons1_current="currentModel.label" @Cbtns_sep="onModel" /></div>
          </div>
          <div class="mc-row">
            <span class="mc-label">现金流选择</span>
            <div class="mc-body">
              <el-select popper-class="irr-select-popper" v-model="inputs.cash_basis" size="mini" class="mc-select" @change="recompute()">
                <el-option v-for="b in cashBases" :key="b.value" :label="b.label" :value="b.value" />
              </el-select>
            </div>
          </div>
          <div class="mc-row">
            <span class="mc-label">要求回报率</span>
            <div class="mc-body">
              <el-input v-model="discountText" size="mini" class="short-input" @change="onDiscount" /><span class="field-unit">%</span>
              <span class="tag">{{ rateSourceLabel }}</span>
              <el-select popper-class="irr-select-popper" v-if="requiredRateOptions.length" :value="''" size="mini" class="mc-select" placeholder="参考回报率"
                @change="applyRequiredRate">
                <el-option v-for="o in requiredRateOptions" :key="o.label" :label="o.label" :value="o.value" />
              </el-select>
              <span v-if="valuation.rate_source === 'custom'" class="irr-help-link" @click="useModelRate">改用模型结果</span>
            </div>
          </div>
          <div class="mc-row mc-sub">
            <span class="mc-label">折现率计算器</span>
            <div class="mc-body">
              <b>{{ pct(valuation.model_rate) }}</b>
              <span class="irr-help-link" @click="calcVisible = !calcVisible">&gt;&gt; 使用 {{ currentModel.calculator }}计算</span>
            </div>
          </div>
          <div v-if="calcVisible" class="rate-panel">
            <div class="rate-panel-head">
              <span class="tag">{{ currentModel.calculator }}</span>
              <span>结果 <b>{{ pct(valuation.model_rate) }}</b></span>
              <span v-if="currentModel.value === 'fcff'">Re 成本（CAPM） <b>{{ pct(valuation.capm) }}</b></span>
            </div>
            <div v-for="key in modelParams" :key="key" class="mc-row">
              <span class="mc-label">{{ rateParams[key].label }}</span>
              <div class="mc-body">
                <el-input v-model="rateText[key]" size="mini" class="short-input" @change="onRate(key)" />
                <span class="field-unit">{{ rateParams[key].ratio === false ? '' : '%' }}</span>
                <el-select popper-class="irr-select-popper" v-if="rateParams[key].ref" :value="''" size="mini" class="mc-select" placeholder="参考指标"
                  @change="(v) => applyReference(key, v)">
                  <el-option v-for="o in referenceOptions(key)" :key="o.label" :label="o.label" :value="o.value" />
                </el-select>
              </div>
            </div>
            <div class="note">{{ currentModel.note }}<template v-if="currentModel.value !== 'ddm' && inputs.mode !== 'full'">；简版未扣除有息负债，
              切换「完整计算」后按市值 + 有息负债计算</template>。参考指标：10 年期国债为中债最新收盘；beta 为相对中证 REITs 指数的 60 日 beta；
              Yield Spread = 修正后分红率 − 10 年期国债 的近三年平均；beta 取近一年平均；Cap Rate 为系统隐含 Cap Rate；同业态取二级业态中位数。
              <template v-if="isConcession"><br />经营权类的分红含本金返还，分红率 − 国债会高估风险溢价，建议参考要求回报率旁的「Rf + 同业态隐含 Spread」
              （按现价买入评估报告现金流的回报 − 10 年期国债）。</template></div>
          </div>
        </div>
      </div>
    </template>

    <el-dialog title="保存方案" :visible.sync="saveDialogVisible" width="360px" append-to-body>
      <el-input v-model="newScenarioName" maxlength="40" placeholder="方案名称" @keyup.enter.native="validName && saveNamed()" />
      <span slot="footer">
        <el-button size="mini" @click="saveDialogVisible = false">取消</el-button>
        <el-button size="mini" type="primary" :disabled="!validName" @click="saveNamed">保存</el-button>
      </span>
    </el-dialog>
    <el-dialog v-if="helpConfig" :title="helpConfig.title" :visible.sync="helpVisible" width="720px" append-to-body>
      <div v-if="helpConfig.content_format === 'html'" class="help-body" v-html="helpConfig.content"></div>
      <div v-else class="help-body help-text">{{ helpConfig.content }}</div>
    </el-dialog>
  </div>
</template>

<script>
import CButtonsSeparate from '@/components/Basic/CButtons_separate';
import { resolveTooltipConfig } from '@/utils/uiTooltipConfig';
import {
  ROWS, FULL_ROWS, MODES, OPERATING_KINDS, PRESETS, CUSTOM_FIELDS, CASH_BASES, MODELS, RATE_PARAMS,
  MODEL_PARAMS, ROW_ACTIONS, GROWTH_SOURCE_LABELS, LAST_SCENARIO, HELP_TOOLTIP_KEY, SOURCE_NOTE,
} from './irrCalculatorConfig';

// conghua_api 的 JSON 把空值写成 ""，这里统一当作没有值
const isBlank = (value) => value === null || value === undefined || value === '';
const parse = (text) => {
  if (text === null || text === undefined || String(text).trim() === '' || String(text).trim() === '--') return null;
  const value = Number(String(text).replace(/,/g, '').replace(/%/g, ''));
  return Number.isFinite(value) ? value : null;
};
const emptyInputs = () => ({
  preset: null, mode: null, model: 'ddm', cash_basis: null, forecast_anchor: true, include_residual: false, overrides: {}, custom: {}, rate: {},
});
const RATE_SOURCE_LABELS = { model: '按模型计算', custom: '自定义', appraisal: '评估报告折现率', default: '默认 5%' };

export default {
  name: 'IrrCalculator',
  components: { CButtonsSeparate },
  props: { code: { type: String, default: '' } },
  data() {
    return {
      loading: false, error: '', baseline: null, result: null, inputs: emptyInputs(),
      remainYearsText: '', discountText: '', customText: {}, rateText: {}, calcVisible: false, assumptionsOpen: false, splitAssets: false,
      rowActions: ROW_ACTIONS, rateParams: RATE_PARAMS,
      scenarios: [], scenarioReadOnly: false, currentScenario: '', savedIrr: null, updateNotice: '',
      saveDialogVisible: false, newScenarioName: '', helpConfig: null, helpVisible: false,
      customFields: CUSTOM_FIELDS, sourceNote: SOURCE_NOTE, requestNumber: 0,
    };
  },
  computed: {
    principalPlan() {
      return this.result && this.result.leverage ? this.result.leverage.principal_plan || null : null
    },
    principalPlanYears() {
      const years = (this.principalPlan && this.principalPlan.years) || []
      return years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : `${years[0] || ''}`
    },
    principalPlanSource() {
      const source = (this.principalPlan && this.principalPlan.source) || {}
      return source.table === 'ai_prospectus_md' ? '招募说明书' : `${source.report_period || ''} 定期报告`
    },
    availablePresets() {
      const available = (this.result && this.result.presets_available) || {};
      return PRESETS.filter((p) => available[p.value] === true || available[p.value] === 'true');
    },
    presetLabels() { return this.availablePresets.map((p) => p.label); },
    currentPreset() { return PRESETS.find((p) => p.value === this.inputs.preset) || PRESETS[2]; },
    currentPresetLabel() { return this.currentPreset.label; },
    presetNote() { return this.currentPreset.note; },
    modeLabels() {
      const available = (this.result && this.result.modes_available) || {};
      return MODES.filter((m) => available[m.value] === true || available[m.value] === 'true').map((m) => m.label);
    },
    currentModeLabel() { return (MODES.find((m) => m.value === (this.inputs.mode || 'simple')) || MODES[0]).label; },
    cashBases() { return CASH_BASES; },
    operatingRows() {
      const op = this.result && this.result.operating;
      const spec = op && OPERATING_KINDS[op.kind];
      if (!spec) {
        // 基金层还没有近四季经营数据的新券：有分项目数据时仍给经营层（开关 + 合计营业收入，打开后按项目展开）
        const assetSpec = this.splitAvailable && OPERATING_KINDS[this.result.assets_kind];
        if (!assetSpec) return [];
        return [...this.assetRows(assetSpec.price, assetSpec.qty, assetSpec),
          { key: 'revenue', label: '总营业收入', kind: 'amount', strong: true, readonly: true }];
      }
      const fields = op.fields || {};
      const editable = true;                       // 任何预测方式下都可逐年改单价 / 增速 / 数量，营业收入随之重算
      const price = spec.price; const qty = spec.qty;
      return [
        { key: 'op_price', label: price.label || price[fields.price], unit: price.unit, kind: 'number', digits: price.digits, editable, op: true },
        { key: 'op_growth', label: spec.growth, kind: 'rate', editable, op: true, opStat: 'price_growth' },
        { key: 'op_qty', label: qty.label || qty[fields.qty], unit: qty.unit, kind: qty.format, digits: qty.digits, editable, op: true,
          opStat: 'qty' },
        ...this.assetRows(price, qty, spec),
        { key: 'revenue', label: '总营业收入', kind: 'amount', strong: true, readonly: true },
      ];
    },
    // 预测所用评估报告的收益法假设，按资产归组；期间写成「2026年–2029年」「2028年起」
    assumptionGroups() {
      const items = ((this.result && this.result.assumptions) || {}).items || [];
      const groups = {};
      items.forEach((item) => {
        const owner = item.owner || '整体';
        const g = groups[owner] || (groups[owner] = { owner, occ: [], rent: [] });
        let period = '';
        if (item.start && item.end) period = item.start === item.end ? item.start : `${item.start}–${item.end}`;
        else if (item.start) period = `${item.start}起`;
        else if (item.end) period = `至${item.end}`;
        (item.code === 'income_occ_assumption' ? g.occ : g.rent).push(period ? `${item.raw}（${period}）` : item.raw);
      });
      return Object.values(groups);
    },
    // 各项目的评估报告假设（按资产编号对应，不按名称）
    assetNotes() {
      const items = ((this.result && this.result.assumptions) || {}).items || [];
      const groups = {};
      items.filter((item) => item.asset_id != null).forEach((item) => {
        const g = groups[item.asset_id] || (groups[item.asset_id] = { occ: [], rent: [] });
        let period = '';
        if (item.start && item.end) period = item.start === item.end ? item.start : `${item.start}–${item.end}`;
        else if (item.start) period = `${item.start}起`;
        else if (item.end) period = `至${item.end}`;
        (item.code === 'income_occ_assumption' ? g.occ : g.rent).push(period ? `${item.raw}（${period}）` : item.raw);
      });
      const out = {};
      Object.entries(groups).forEach(([id, g]) => {
        out[id] = [g.occ.length ? `出租率：${g.occ.join('；')}` : '', g.rent.length ? `租金增长率：${g.rent.join('；')}` : '']
          .filter(Boolean).join('  ');
      });
      return out;
    },
    splitAvailable() { return !!(this.result && this.result.assets && this.result.assets.length >= 2); },
    tableSections() {
      const sections = [];
      if (this.operatingRows.length && !this.result.yield_only) {
        sections.push({ key: 'op', title: '主要经营层面计算', rows: this.operatingRows, split: this.splitAvailable,
          hint: this.inputs.preset === 'operating' ? '营业收入 = 最新实际 × 单价 × 数量 的变化'
            : '未单独预测经营指标时按营业收入反算（数量保持最新实际）；改单价、增速或数量，该年营业收入随之重算' });
      }
      const rows = this.inputs.mode === 'full' ? FULL_ROWS : ROWS.filter((r) => !r.leverage || this.result.leverage);
      sections.push({ key: 'cash', title: '主要现金流计算', rows });
      return sections;
    },
    valuation() { return (this.result && this.result.valuation) || {}; },
    availableModels() {
      const available = this.valuation.models_available || {};
      return MODELS.filter((m) => available[m.value] === true || available[m.value] === 'true');
    },
    modelLabels() { return this.availableModels.map((m) => m.label); },
    currentModel() { return MODELS.find((m) => m.value === (this.valuation.model || this.inputs.model)) || MODELS[0]; },
    modelParams() { return MODEL_PARAMS[this.currentModel.value] || []; },
    rateSourceLabel() { return RATE_SOURCE_LABELS[this.valuation.rate_source] || ''; },
    isConcession() { return this.valuation.models_available && this.valuation.models_available.nav === false; },
    requiredRateOptions() {
      const refs = this.valuation.references || {};
      const rf = ((refs.rf || {}).value);
      const spread = refs.implied_spread || {};
      const options = [];
      if (!isBlank(rf) && !isBlank(spread.peer)) {
        options.push({ label: `Rf + 同业态隐含 Spread ${this.pct(rf + spread.peer)}`, value: rf + spread.peer });
      }
      if (!isBlank(rf) && !isBlank(spread.own)) {
        options.push({ label: `Rf + 本基金隐含 Spread ${this.pct(rf + spread.own)}`, value: rf + spread.own });
      }
      if (!isBlank(this.valuation.appraisal_rate)) {
        options.push({ label: `评估报告折现率 ${this.pct(this.valuation.appraisal_rate)}`, value: this.valuation.appraisal_rate });
      }
      return options;
    },
    historyColumns() { return (this.result && this.result.history_columns) || []; },
    residualAmount() { return this.result && this.result.residual && !isBlank(this.result.residual.amount) ? this.result.residual.amount : null; },
    systemIrr() {
      const irr = (this.result && this.result.system_irr) || {};
      return this.inputs.include_residual && !isBlank(irr.irr_single_res)
        ? irr.irr_single_res : irr.irr_single;
    },
    namedScenarios() { return this.scenarios.filter((s) => s.name !== LAST_SCENARIO); },
    validName() { const n = this.newScenarioName.trim(); return n && n !== LAST_SCENARIO && !/[/\\]/.test(n); },
    forecastSourceText() {
      if (this.inputs.preset !== 'forecast') return '';
      const source = this.baseline && this.baseline.forecast && this.baseline.forecast.source;
      if (!source) return '';
      return source.valuation_date ? `（评估基准日 ${source.valuation_date}）` : '';
    },
    forecastCheckNotice() {
      const check = this.result && this.inputs.preset === 'forecast' && this.result.forecast_check;
      if (!check || isBlank(check.ratio)) return '';
      const gap = (Number(check.ratio) - 1) * 100;
      const wan = (v) => (Number(v) / 10000).toLocaleString(undefined, { maximumFractionDigits: 0 });
      return `评估报告对 ${check.year} 年营业收入的预测为 ${wan(check.forecast)} 万元，实际 ${wan(check.actual)} 万元，`
        + `预测比实际${gap >= 0 ? '高' : '低'} ${Math.abs(gap).toFixed(1)}%。`;
    },
    jumpNotice() {
      const jump = this.result && this.result.forecast_jump;
      if (isBlank(jump)) return '';
      return `评估报告首个预测年营业收入为最新实际 TTM 的 ${Number(jump).toFixed(2)} 倍，`
        + '可能因预测含扩募资产、项目公司与基金口径不同或预测偏乐观，可勾选「按最新实际校准」。';
    },
  },
  watch: { code() { this.load(); } },
  mounted() {
    resolveTooltipConfig(HELP_TOOLTIP_KEY).then((config) => { this.helpConfig = config; });
    this.load();
  },
  methods: {
    effectiveCode() { return this.code || window.sessionStorage.getItem('CURRENTCODE'); },
    async load() {
      const number = ++this.requestNumber;
      this.loading = true; this.error = ''; this.result = null; this.updateNotice = ''; this.savedIrr = null;
      this.inputs = emptyInputs(); this.currentScenario = '';
      try {
        const { data } = await this.$https.get('/api/v2/irr_calc/baseline', { params: { reit_code: this.effectiveCode() } });
        if (number !== this.requestNumber) return;
        if (data.code !== 200) { this.error = data.msg; return; }
        this.baseline = data.data.baseline;
        this.setResult(data.data.result);
        await this.loadScenarios();
        const last = this.scenarios.find((s) => s.name === LAST_SCENARIO);
        if (last) await this.applyScenario(last);
      } catch (e) {
        this.error = 'IRR 计算器加载失败，请稍后重试';
      } finally {
        if (number === this.requestNumber) this.loading = false;
      }
    },
    setResult(result) {
      if (result && result.error) { this.error = result.error; return; }
      this.result = result;
      this.inputs.preset = result.preset;
      this.inputs.mode = result.mode || 'simple';
      this.inputs.forecast_anchor = result.forecast_anchor !== false && result.forecast_anchor !== '';
      this.inputs.cash_basis = result.cash_basis;
      this.remainYearsText = Number(result.remain_years).toFixed(2);
      if (result.npv && !isBlank(result.npv.discount_rate)) this.discountText = (result.npv.discount_rate * 100).toFixed(2);
      const valuation = result.valuation || {};
      if (valuation.model) this.inputs.model = valuation.model;
      if (valuation.cash_basis) this.inputs.cash_basis = valuation.cash_basis;
      const params = valuation.params || {};
      this.rateText = Object.fromEntries(Object.keys(RATE_PARAMS).map((k) => [k, isBlank(params[k]) ? ''
        : (RATE_PARAMS[k].ratio === false ? Number(params[k]).toFixed(2) : (params[k] * 100).toFixed(2))]));
      const custom = result.custom || {};
      this.customText = Object.fromEntries(CUSTOM_FIELDS.map((f) => [f.key, isBlank(custom[f.key]) ? '' : (custom[f.key] * 100).toFixed(2)]));
    },
    requestInputs() {
      const inputs = { ...this.inputs };
      if (!Object.keys(inputs.overrides || {}).length) delete inputs.overrides;
      if (!Object.keys(inputs.custom || {}).length) delete inputs.custom;
      if (!Object.keys(inputs.rate || {}).length) delete inputs.rate;
      if (!inputs.cash_basis) delete inputs.cash_basis;
      return inputs;
    },
    async recompute(autosave = true) {
      this.loading = true; this.error = '';
      try {
        const { data } = await this.$https.post('/api/v2/irr_calc/compute',
          { reit_code: this.effectiveCode(), inputs: this.requestInputs() });
        if (data.code !== 200) { this.error = data.msg; return; }
        this.setResult(data.data);
        if (autosave !== false) this.save(LAST_SCENARIO, true);
      } catch (e) {
        this.error = '计算失败，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    onPreset(label) {
      const preset = PRESETS.find((p) => p.label === label);
      if (!preset || preset.value === this.inputs.preset) return;
      this.inputs = { ...this.inputs, preset: preset.value };
      this.recompute();
    },
    onCustom(key) {
      const value = parse(this.customText[key]);
      const custom = { ...(this.inputs.custom || {}) };
      if (value === null) delete custom[key]; else custom[key] = value / 100;
      this.inputs = { ...this.inputs, custom };
      this.recompute();
    },
    editable(row, index) {
      if (!row.editable || row.readonly || this.result.yield_only) return false;
      return index > 0;                                    // 第 0 期是最新实际，不可改
    },
    isEdited(key, year) { return ((this.inputs.overrides[key] || {})[String(year)]) !== undefined; },
    // 键盘：回车 / Shift+回车 = 确认并移到右 / 左一格，上下方向键 = 同一年的上 / 下一个可改的格子，Esc = 放弃本格修改
    onCellKey(row, col, event) {
      const input = event.target;
      if (event.key === 'Escape') {
        input.value = this.display(row, this.cell(row, col));
        input.blur();
        return;
      }
      const moves = { Enter: event.shiftKey ? [0, -1] : [0, 1], ArrowUp: [-1, 0], ArrowDown: [1, 0] };
      const move = moves[event.key];
      if (!move) return;
      event.preventDefault();
      const cells = Array.from(this.$el.querySelectorAll('input.cell-input'));
      const rows = [...new Set(cells.map((c) => c.dataset.row))];
      const target = move[0]
        ? cells.find((c) => c.dataset.row === rows[rows.indexOf(row.key) + move[0]] && Number(c.dataset.col) === col)
        : cells.find((c) => c.dataset.row === row.key && Number(c.dataset.col) === col + move[1]);
      if (target) { target.focus(); target.select(); } else { input.blur(); }
    },
    onCell(row, year, text) {
      let value = parse(text);
      if (value !== null && row.kind === 'rate') value /= 100;
      if (value !== null && row.kind === 'amount') value *= 10000;
      const cells = { ...(this.inputs.overrides[row.key] || {}) };
      if (value === null) delete cells[String(year)]; else cells[String(year)] = value;
      const base = value === null ? this.inputs.overrides : this.withoutConflicts(this.inputs.overrides, row.key, [String(year)]);
      const overrides = { ...base, [row.key]: cells };
      if (!Object.keys(cells).length) delete overrides[row.key];
      this.inputs = { ...this.inputs, overrides };
      this.recompute();
    },
    onRemainYears() {
      const value = parse(this.remainYearsText);
      this.inputs = { ...this.inputs, remain_years: value && value > 0 ? value : undefined };
      this.recompute();
    },
    onDiscount() {
      const value = parse(this.discountText);
      this.inputs = { ...this.inputs, discount_rate: value === null ? undefined : value / 100 };
      this.recompute();
    },
    onMode(label) {
      const mode = MODES.find((m) => m.label === label);
      if (!mode || mode.value === this.inputs.mode) return;
      this.inputs = { ...this.inputs, mode: mode.value, cash_basis: null };
      this.recompute();
    },
    // 按项目拆分：每个项目 租金 / 增幅 / 出租率 / 营业收入 四行（行键 asset:{编号}:字段，计算在 conghua_api）
    assetRows(price, qty, spec) {
      if (!this.splitAssets || !this.splitAvailable) return [];
      return this.result.assets.flatMap((a) => {
        const k = `asset:${a.asset_num}:`;
        return [
          { key: k + 'op_price', label: `${a.name}｜${price.label || price.p_face}`, unit: price.unit, kind: 'number', digits: price.digits, editable: true, op: true, asset: true,
            note: a.asset_id != null ? this.assetNotes[String(a.asset_id)] : undefined },
          { key: k + 'op_growth', label: `${a.name}｜${spec.growth}`, kind: 'rate', editable: true, op: true, asset: true },
          { key: k + 'op_qty', label: `${a.name}｜${qty.label || qty.q_avg}`, unit: qty.unit, kind: qty.format, digits: qty.digits, editable: true, op: true, asset: true },
          { key: k + 'revenue', label: `${a.name}｜营业收入`, kind: 'amount', editable: true, asset: true, assetTotal: true },
        ];
      });
    },
    // 同一年改合计（营业收入 / 增速 / 基金层经营指标）就清掉该年各项目的改动，改项目就清掉该年合计的改动：以最后一次为准
    withoutConflicts(overrides, key, years) {
      const totals = ['revenue', 'growth', 'op_price', 'op_growth', 'op_qty'];
      const isAsset = key.startsWith('asset:');
      if (!isAsset && !totals.includes(key)) return overrides;
      const out = { ...overrides };
      Object.keys(out).forEach((k) => {
        if (k === key || (isAsset ? !totals.includes(k) : !k.startsWith('asset:'))) return;
        const cells = { ...out[k] };
        years.forEach((y) => delete cells[y]);
        if (Object.keys(cells).length) out[k] = cells; else delete out[k];
      });
      return out;
    },
    cell(row, index) {
      const values = this.result.rows[row.key];
      if (!values) return null;
      return values[index];
    },
    onModel(label) {
      const model = MODELS.find((m) => m.label === label);
      if (!model || model.value === this.inputs.model) return;
      // 换模型：现金流口径与折现率回到该模型的默认（客户填过的模型参数保留）
      this.inputs = { ...this.inputs, model: model.value, cash_basis: null, discount_rate: undefined };
      this.recompute();
    },
    applyRequiredRate(value) {
      this.discountText = (value * 100).toFixed(2);
      this.onDiscount();
    },
    useModelRate() {
      this.inputs = { ...this.inputs, discount_rate: undefined };
      this.recompute();
    },
    onRate(key) {
      const value = parse(this.rateText[key]);
      const rate = { ...(this.inputs.rate || {}) };
      if (value === null) delete rate[key]; else rate[key] = RATE_PARAMS[key].ratio === false ? value : value / 100;
      this.inputs = { ...this.inputs, rate, discount_rate: undefined };
      this.recompute();
    },
    referenceOptions(key) {
      const param = RATE_PARAMS[key];
      const ref = (this.valuation.references || {})[param.ref] || {};
      return Object.entries(param.refLabels).filter(([field]) => !isBlank(ref[field])).map(([field, label]) => {
        const value = Number(ref[field]);
        const shown = param.ratio === false ? value.toFixed(2) : (value * 100).toFixed(2) + '%';
        return { label: `${label} ${shown}`, value };
      });
    },
    applyReference(key, value) {
      this.rateText = { ...this.rateText, [key]: RATE_PARAMS[key].ratio === false ? value.toFixed(2) : (value * 100).toFixed(2) };
      this.onRate(key);
    },
    forecastYears() { return this.result.years.slice(1); },
    rowValues(row) { return (this.result.rows[row.key] || []).slice(1); },
    setRowOverrides(row, valuesByYear) {
      const cells = { ...(this.inputs.overrides[row.key] || {}) };
      Object.entries(valuesByYear).forEach(([year, value]) => {
        if (value === null || value === undefined) delete cells[year]; else cells[year] = value;
      });
      const set = Object.entries(valuesByYear).filter(([, v]) => v !== null && v !== undefined).map(([y]) => y);
      const overrides = { ...this.withoutConflicts(this.inputs.overrides, row.key, set), [row.key]: cells };
      if (!Object.keys(cells).length) delete overrides[row.key];
      this.inputs = { ...this.inputs, overrides };
      this.recompute();
    },
    async onRowAction(row, command) {
      const years = this.forecastYears(); const values = this.rowValues(row);
      if (command === 'fill') {
        const first = values[0];
        if (isBlank(first)) return;
        this.setRowOverrides(row, Object.fromEntries(years.map((y) => [String(y), Number(first)])));
      } else if (command === 'scale') {
        let answer;
        try {
          answer = await this.$prompt('输入调整比例（%），例如 50 表示整行乘以 50%', `${row.label} 按比例调整`, {
            inputValue: '100', inputPattern: /^\s*-?\d+(\.\d+)?\s*%?\s*$/, inputErrorMessage: '请输入数字',
          });
        } catch (e) { return; }
        const factor = parse(answer.value) / 100;
        this.setRowOverrides(row, Object.fromEntries(years.map((y, i) => [String(y),
          isBlank(values[i]) ? null : Number(values[i]) * factor])));
      } else if (command === 'copy') {
        const text = [row.label, ...values.map((v) => this.display(row, v))].join('\t');
        try { await navigator.clipboard.writeText(text); this.$message.success('已复制，可直接粘贴到 Excel'); } catch (e) {
          this.$message.warning('浏览器未允许写入剪贴板');
        }
      } else if (command === 'reset') {
        this.setRowOverrides(row, Object.fromEntries(Object.keys(this.inputs.overrides[row.key] || {}).map((y) => [y, null])));
      }
    },
    onPaste(row, index, event) {
      const text = (event.clipboardData || window.clipboardData).getData('text');
      const parts = String(text).trim().split(/\t|\r?\n/).map((p) => p.trim()).filter((p) => p !== '');
      if (parts.length < 2) return;                        // 单个数照常粘贴，由 change 处理
      event.preventDefault();
      const years = this.result.years;
      const cells = {};
      parts.forEach((part, k) => {
        const year = years[index + k];
        let value = parse(part);
        if (year === undefined || value === null) return;
        if (row.kind === 'rate') value /= 100;
        if (row.kind === 'amount') value *= 10000;
        cells[String(year)] = value;
      });
      this.setRowOverrides(row, cells);
    },
    async loadScenarios() {
      try {
        const { data } = await this.$https.get('/api/v2/irr_calc/scenarios', { params: { reit_code: this.effectiveCode() } });
        if (data.code !== 200) { this.scenarios = []; this.scenarioReadOnly = true; return; }
        this.scenarios = data.data.scenarios || [];
        this.scenarioReadOnly = !!data.data.read_only;
      } catch (e) {
        this.scenarios = []; this.scenarioReadOnly = true;
      }
    },
    async applyScenario(scenario) {
      const saved = scenario.inputs || {};
      this.inputs = {
        ...emptyInputs(), ...saved, overrides: { ...(saved.overrides || {}) }, custom: { ...(saved.custom || {}) },
        rate: { ...(saved.rate || {}) },
      };
      if (this.inputs.remain_years) this.remainYearsText = Number(this.inputs.remain_years).toFixed(2);
      if (this.inputs.discount_rate !== undefined) this.discountText = (this.inputs.discount_rate * 100).toFixed(2);
      await this.recompute(false);
      if (this.error && saved.preset) {                    // 保存时的预测方式现在不可用（例如评估报告被替换）：退回默认方式
        this.inputs = { ...this.inputs, preset: null };
        await this.recompute(false);
      }
      this.savedIrr = scenario.result && !isBlank(scenario.result.irr) ? scenario.result.irr : null;
      this.updateNotice = '';
      if (this.result && scenario.structure_version !== this.result.structure_version) {
        const edited = Object.values(this.inputs.overrides).reduce((n, cells) => n + Object.keys(cells).length, 0);
        const replaced = (this.result.replaced || []).length;
        const [savedThrough] = String(scenario.structure_version).split('|');
        this.updateNotice = `基础数据已更新（保存时实际截至 ${savedThrough === '-' ? '无季报' : savedThrough}，`
          + `现为 ${this.result.actual_through || '无季报'}）：已从最新实际重新滚动，你修改的 ${edited - replaced} 个格子已保留`
          + (replaced ? `，${replaced} 个所在年份已成为实际，改用实际数` : '') + '。';
      }
    },
    openScenario(name) {
      const scenario = this.scenarios.find((s) => s.name === name);
      if (scenario) this.applyScenario(scenario);
    },
    async save(name, silent) {
      if (this.scenarioReadOnly || !this.result) return;
      try {
        const { data } = await this.$https.put(`/api/v2/irr_calc/scenarios/${encodeURIComponent(name)}`, {
          reit_code: this.effectiveCode(), mode: 'simple', structure_version: this.result.structure_version,
          inputs: this.requestInputs(), result: { irr: this.result.irr, npv: this.result.npv, as_of: this.result.as_of },
        });
        if (data.code !== 200) { if (!silent) this.$message.error(data.msg); return; }
        if (!silent) { this.$message.success('方案已保存'); await this.loadScenarios(); this.currentScenario = name; }
      } catch (e) {
        if (!silent) this.$message.error('方案保存失败，请稍后重试');
      }
    },
    async saveNamed() {
      const name = this.newScenarioName.trim();
      this.saveDialogVisible = false;
      await this.save(name, false);
      this.newScenarioName = '';
    },
    async removeScenario() {
      const name = this.currentScenario;
      const { data } = await this.$https.delete(`/api/v2/irr_calc/scenarios/${encodeURIComponent(name)}`,
        { params: { reit_code: this.effectiveCode() } });
      if (data.code !== 200) { this.$message.error(data.msg); return; }
      this.currentScenario = '';
      await this.loadScenarios();
    },
    resetInputs() {
      this.inputs = emptyInputs(); this.savedIrr = null; this.updateNotice = '';
      this.currentScenario = '';
      this.recompute();
    },
    statValue(statKey, key) {
      const stats = (((this.baseline || {}).history || {}).stats || {})[statKey];
      return stats && !isBlank(stats[key]) ? this.pct(stats[key]) : '--';
    },
    stat(row, key) {
      if (row.opStat) {
        const stats = (((this.result || {}).operating || {}).stats || {})[row.opStat];
        return stats && !isBlank(stats[key]) ? this.display(row, stats[key]) : '--';
      }
      return row.stat && this.baseline ? this.statValue(row.stat, key) : '';
    },
    display(row, value) {
      if (isBlank(value)) return row.key === 'growth' ? '' : '--';
      if (row.kind === 'rate') return (value * 100).toFixed(2) + '%';
      if (row.kind === 'number') {
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: row.digits || 0, maximumFractionDigits: row.digits || 0 });
      }
      return (value / 10000).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    growthSourceLabel(index) {
      return GROWTH_SOURCE_LABELS[(this.result.rows.growth_source || [])[index]] || '';
    },
    premium(ratio) {
      if (isBlank(ratio)) return '--';
      const diff = (Number(ratio) - 1) * 100;
      return `${diff >= 0 ? '溢价' : '折价'} ${Math.abs(diff).toFixed(1)}%`;
    },
    pct(value) { return isBlank(value) ? '--' : (value * 100).toFixed(2) + '%'; },
    num(value, digits) { return isBlank(value) ? '--' : Number(value).toFixed(digits); },
  },
};
</script>

<style scoped lang="less">
@fs: calc(var(--ROOTFONTFIZE) * (14 / 144));
@fs-small: calc(var(--ROOTFONTFIZE) * (12 / 144));

.irr-calculator { position: relative; padding: 0 0 calc(var(--ROOTFONTFIZE) * (20 / 144)); color: var(--chart_title); font-size: @fs; }
.assistantTitle {
  font-size: calc(var(--ROOTFONTFIZE) * (20 / 144));
  font-weight: 580;
  color: var(--chart_title);
  margin-bottom: calc(var(--ROOTFONTFIZE) * (16 / 144));
}
.results-title { margin-top: calc(var(--ROOTFONTFIZE) * (28 / 144)); }
.irr-help-link { margin-left: 16px; font-size: @fs-small; font-weight: 400; color: var(--primary-color, rgb(64, 158, 255)); cursor: pointer; }
.toolbar { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px; }
.toolbar-row, .custom-strip { display: flex; flex-wrap: wrap; gap: 10px 28px; align-items: center; }
.custom-strip { margin-bottom: 12px; }
.custom-strip {
  padding: 10px 14px;
  background-color: var(--charts_bgc);
  border: 1px solid var(--charts_brc);
  border-radius: calc(var(--ROOTFONTFIZE) * (5 / 144));
}
.field { display: flex; align-items: center; gap: 6px; }
.field-label { color: var(--stock_area_item_color2); }
.field-unit { margin-left: 2px; color: var(--stock_area_item_color2); }
.hint { font-size: @fs-small; color: var(--item_date_color); }
.short-input { width: 76px; }
.basis-select { width: 240px; }
.scenario-select { width: 150px; }
.scenario { margin-left: auto; }
.notice { color: var(--stock_area_item_color2); font-size: @fs-small; margin: 6px 0; }
.update-notice { color: #e6a23c; }

.section-card {
  margin-bottom: calc(var(--ROOTFONTFIZE) * (14 / 144));
  padding: calc(var(--ROOTFONTFIZE) * (12 / 144)) calc(var(--ROOTFONTFIZE) * (14 / 144));
  background-color: var(--charts_bgc);
  border: 1px solid var(--charts_brc);
  border-radius: calc(var(--ROOTFONTFIZE) * (5 / 144));
}
.table-wrap { overflow-x: auto; border: 1px solid var(--table_brc); border-radius: 4px; }
.irr-table { border-collapse: collapse; font-size: @fs-small; min-width: 100%; color: var(--table_color); font-variant-numeric: tabular-nums; }
.irr-table th, .irr-table td {
  padding: 6px 8px; text-align: right; white-space: nowrap;
  border-bottom: 1px solid var(--table_brc); background-color: var(--table_bgc);
}
.irr-table th { font-weight: 600; text-align: center; background-color: var(--table_th_bgc); color: var(--chart_title); }
.irr-table .sub { display: block; font-weight: 400; font-size: 11px; color: var(--item_date_color); }
.irr-table .sticky { position: sticky; z-index: 1; }
.irr-table .label-col { left: 0; min-width: 150px; text-align: left; }
.irr-table .stat-col { min-width: 78px; color: var(--item_date_color); }
.irr-table th.stat-col:nth-child(2), .irr-table td.stat-col:nth-child(2) { left: 150px; }
.irr-table th.stat-col:nth-child(3), .irr-table td.stat-col:nth-child(3) { left: 228px; }
.irr-table th.stat-col:nth-child(4), .irr-table td.stat-col:nth-child(4) { left: 306px; border-right: 1px solid var(--table_brc); }
.irr-table td.actual-col, .irr-table th.actual-col { background-color: var(--table_th_2_bgc); }
.irr-table tr.strong td { font-weight: 600; }
.irr-table tr.ratio td:not(.sticky) { color: var(--stock_area_item_color2); }
.irr-table tbody tr:hover td { background-color: var(--table_bgc_hover); }
.irr-table tbody tr:hover td.edited { background-color: rgba(230, 162, 60, 0.3); }
.irr-table tr.highlight td:not(.sticky) { color: var(--primary-color, rgb(64, 158, 255)); font-weight: 600; }
.irr-table td.edited { background-color: rgba(230, 162, 60, 0.22); }
.cell-input {
  width: 72px; background: transparent; border: 1px solid transparent; border-bottom: 1px dashed var(--charts_brc);
  border-radius: 2px; color: inherit; text-align: right; font: inherit; padding: 1px 4px;
}
.cell-input:hover { border-color: var(--charts_brc); }
.cell-input:focus { outline: none; border: 1px solid var(--primary-color, rgb(64, 158, 255)); background: var(--table_bgc); }
.section-title { margin: 0 0 10px; font-size: calc(var(--ROOTFONTFIZE) * (15 / 144)); font-weight: 600; color: var(--chart_title); }
.section-title .hint { margin-left: 12px; font-weight: 400; }
.irr-table .unit { margin-left: 4px; font-size: 11px; color: var(--item_date_color); }
.legend { display: flex; flex-wrap: wrap; gap: 4px 16px; align-items: center; font-size: @fs-small; color: var(--item_date_color); margin: 0; line-height: 1.7; }
.legend .irr-help-link { margin-left: 0; }
.legend-key { display: inline-flex; align-items: center; gap: 6px; }
.swatch { display: inline-block; width: 12px; height: 12px; border-radius: 2px; border: 1px solid var(--charts_brc); }
.swatch.actual { background-color: var(--table_th_2_bgc); }
.swatch.edited { background-color: rgba(230, 162, 60, 0.4); }

.results { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr); gap: calc(var(--ROOTFONTFIZE) * (20 / 144)); }
.result-card {
  padding: calc(var(--ROOTFONTFIZE) * (16 / 144)) calc(var(--ROOTFONTFIZE) * (20 / 144));
  background-color: var(--charts_bgc);
  border: 1px solid var(--charts_brc);
  border-radius: calc(var(--ROOTFONTFIZE) * (5 / 144));
}
.card-title { font-size: calc(var(--ROOTFONTFIZE) * (16 / 144)); font-weight: 700; color: var(--chart_title); margin-bottom: 12px; }
.summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 24px; }
.metric { display: flex; flex-direction: column; gap: 4px; }
.metric span { color: var(--stock_area_item_color2); font-size: @fs-small; }
.metric b { color: var(--chart_title); font-size: calc(var(--ROOTFONTFIZE) * (18 / 144)); font-weight: 600; }
.metric b.headline { font-size: calc(var(--ROOTFONTFIZE) * (28 / 144)); color: var(--primary-color, rgb(64, 158, 255)); }
.note-mark { margin-left: 6px; padding: 0 4px; border: 1px solid var(--charts_brc); border-radius: 3px; font-size: 11px; color: var(--item_date_color); cursor: help; }
.split-toggle { margin-left: 12px; }
/deep/ .split-toggle .el-checkbox__label { font-size: 12px; }
.assumptions { padding: 6px 10px 2px; font-size: 12px; color: var(--item_date_color); }
.assumptions ul { margin: 6px 0 0; padding-left: 16px; }
.assumptions li { margin-bottom: 4px; line-height: 1.6; }
.assumptions li b { color: var(--table_color); margin-right: 8px; font-weight: 500; }
.assumptions li span { margin-right: 12px; }
.metric em { font-style: normal; font-size: 11px; color: var(--item_date_color); }
.tag { padding: 1px 8px; border: 1px solid var(--charts_brc); border-radius: 3px; font-size: @fs-small; color: var(--stock_area_item_color2); }
.rate-panel { margin-top: 8px; padding: 10px 14px; border: 1px solid var(--charts_brc); border-radius: calc(var(--ROOTFONTFIZE) * (5 / 144)); }
.rate-panel-head { display: flex; gap: 20px; align-items: center; margin-bottom: 4px; }
.rate-panel-head b { color: var(--primary-color, rgb(64, 158, 255)); }
.ref-select { width: 220px; }
.note { font-size: 11px; color: var(--item_date_color); line-height: 1.6; margin-top: 6px; }
.row-menu { margin-left: 6px; visibility: hidden; }
.irr-table tr:hover .row-menu { visibility: visible; }
.row-menu-trigger { cursor: pointer; color: var(--primary-color, rgb(64, 158, 255)); padding: 0 4px; }
.table-wrap::-webkit-scrollbar { height: 8px; }
.table-wrap::-webkit-scrollbar-track { background: transparent; }
.table-wrap::-webkit-scrollbar-thumb { background: var(--charts_brc); border-radius: 4px; }
.table-wrap::-webkit-scrollbar-thumb:hover { background: var(--item_date_color); }
.table-wrap { scrollbar-width: thin; scrollbar-color: var(--charts_brc) transparent; }
.kv { display: flex; align-items: center; gap: 8px; margin: 8px 0; }
.kv > span:first-child { flex: 0 0 120px; min-width: 120px; color: var(--stock_area_item_color2); }
/* 模型卡：标签列 | 控件列 两栏，所有控件从同一竖线起；折现率计算器作为「要求回报率」的子步骤缩进 */
.mc-row { display: grid; grid-template-columns: 110px 1fr; align-items: center; gap: 12px; margin: 10px 0; }
.mc-label { color: var(--stock_area_item_color2); }
.mc-body { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-width: 0; }
.mc-body b { color: var(--chart_title); font-weight: 600; }
.mc-select { width: 200px; }
.model-card .mc-body /deep/ .container { margin-left: 0; }
.model-card .mc-row { min-height: 32px; margin: 8px 0; }
.model-card .mc-body /deep/ .el-input__inner, .model-card .mc-body /deep/ .el-button { height: 28px; line-height: 28px; }
.model-card .tag { padding: 0 6px; }
.mc-sub { margin-top: 2px; padding-left: 12px; border-left: 2px solid var(--charts_brc); }
.model-card .rate-panel { margin: 4px 0 0 12px; padding: 8px 12px; border: none; border-left: 2px solid var(--charts_brc); border-radius: 0; background: var(--Btn-bgc); }
.model-card .rate-panel .mc-row { grid-template-columns: 98px 1fr; }
.kv b { color: var(--chart_title); font-weight: 600; }
.help-body { line-height: 1.7; font-size: 13px; }
.help-text { white-space: pre-wrap; }
/* 控件不画外框：填充用主题按钮底色，和站内其他按钮/下拉一致(浅深色跟变量走)。 */
/deep/ .el-input__inner { background: var(--Btn-bgc); color: var(--table_color); border: none; text-align: center; padding: 0 5px; }
/deep/ .el-input__inner:focus, /deep/ .el-select .el-input.is-focus .el-input__inner { box-shadow: 0 0 0 1px var(--primary-color, rgb(64, 158, 255)); }
/deep/ .el-button--default {
  background: var(--Btn-bgc); color: var(--stock_area_item_color2); border: none;
}
/deep/ .el-button--default:hover, /deep/ .el-button--default:focus {
  background: var(--Btn-bgc); color: var(--primary-color, rgb(64, 158, 255)); border: none;
}
/deep/ .el-button--default.is-disabled, /deep/ .el-button--default.is-disabled:hover {
  background: var(--Btn-bgc); color: var(--item_date_color); border: none; opacity: 0.6;
}
/deep/ .el-checkbox__inner { background: transparent; border-color: var(--charts_brc); }
/deep/ .el-checkbox__label { color: var(--stock_area_item_color2); }
@media (max-width: 1100px) { .results { grid-template-columns: 1fr; } }
</style>
<style>
/* IRR 页下拉挂在 body 上，scoped 样式够不着；按 CSelect 同一套 Select 变量写，深浅色一起适配。 */
.irr-select-popper.el-popper { background-color: var(--Select-bgc2); border: none; }
.irr-select-popper .popper__arrow, .irr-select-popper .popper__arrow::after { border-bottom-color: var(--Select-bgc2) !important; border-top-color: var(--Select-bgc2) !important; }
.irr-select-popper .el-select-dropdown__item { background-color: var(--Select-bgc2); color: var(--Select-color); }
.irr-select-popper .el-select-dropdown__item.hover, .irr-select-popper .el-select-dropdown__item:hover { background-color: var(--Select-bgc); }
.irr-select-popper .el-select-dropdown__item.selected { color: var(--primary-color, rgb(64, 158, 255)); font-weight: 600; background-color: var(--Select-bgc2); }
</style>
